/**
 * Helpers for the Training Room Auth0 → www.ept.ai handoff page.
 */

export const DEFAULT_TRAINING_RETURN_TO = 'https://www.ept.ai/training-room/';

/** Same-origin path restored after Auth0 (query restored from sessionStorage). */
export const TRAINING_AUTH_SYNC_PATH = '/training-auth-sync';

const TRAINING_RETURN_TO_STORAGE_KEY = 'training_auth_return_to';

const ALLOWED_RETURN_TO_HOSTS = new Set([
  'www.ept.ai',
  'ept.ai',
  'localhost',
  '127.0.0.1',
]);

/**
 * Resolve a safe external Training Room return URL.
 *
 * Example: `https://www.ept.ai/training-room/catalog/` -> same URL;
 * `https://evil.com/` -> DEFAULT_TRAINING_RETURN_TO.
 */
export function resolveTrainingExternalReturnTo(rawReturnTo: string | null): string {
  if (!rawReturnTo) {
    return DEFAULT_TRAINING_RETURN_TO;
  }
  try {
    const parsed = new URL(rawReturnTo);
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
      return DEFAULT_TRAINING_RETURN_TO;
    }
    if (!ALLOWED_RETURN_TO_HOSTS.has(parsed.hostname)) {
      return DEFAULT_TRAINING_RETURN_TO;
    }
    return parsed.toString();
  } catch {
    return DEFAULT_TRAINING_RETURN_TO;
  }
}

/**
 * Persist external returnTo across the Auth0 full-page redirect.
 *
 * Example: store `https://www.ept.ai/training-room/` before loginWithRedirect.
 */
export function storeTrainingReturnTo(externalReturnTo: string): void {
  try {
    sessionStorage.setItem(TRAINING_RETURN_TO_STORAGE_KEY, externalReturnTo);
  } catch {
    // sessionStorage may be unavailable; query param remains best-effort.
  }
}

/**
 * Read the persisted Training Room returnTo without clearing it.
 *
 * Example: after Auth0 returns to `/training-auth-sync` -> stored www URL.
 */
export function peekTrainingReturnTo(): string | null {
  try {
    return sessionStorage.getItem(TRAINING_RETURN_TO_STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Clear the persisted Training Room returnTo after a successful handoff starts.
 */
export function clearTrainingReturnTo(): void {
  try {
    sessionStorage.removeItem(TRAINING_RETURN_TO_STORAGE_KEY);
  } catch {
    // no-op
  }
}

/**
 * Same-origin Auth0 appState path. Query is intentionally omitted so Auth0
 * appState stays a simple path; external returnTo is kept in sessionStorage.
 *
 * Example: -> `/training-auth-sync`
 */
export function buildTrainingSyncAppStateReturnTo(): string {
  return TRAINING_AUTH_SYNC_PATH;
}
